import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { Address, createAddress, IFormModel } from '@mwe/models';
import { FormComponent } from '../form/form.component';
import { SelectFirstInvalidInputDirective } from '../form/inputs/select-first-invalid-input/select-first-invalid-input.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs/operators';

const INPUT_MIN_LENGTH = 1;
const INPUT_LOWER_MAX_LENGTH = 10;
const INPUT_HIGHER_MAX_LENGTH = 30;

@Component({
  selector: 'mwe-manual-address-input',
  templateUrl: './manual-address-input.component.html',
  imports: [FormComponent, SelectFirstInvalidInputDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManualAddressInputComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly initialData = input<Address>();
  readonly formChanged = output<boolean>();

  readonly formRef = viewChild<FormComponent>(FormComponent);
  readonly formAddress = signal<Address>(null);

  formModel: IFormModel;

  ngOnInit(): void {
    this.createFormModel();
  }

  ngAfterViewInit(): void {
    this.formRef()
      ?.form?.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.formAddress.set(this.getAddressFromForm())),
        tap(() => this.formChanged.emit(true)),
      )
      .subscribe();
  }

  getAddressFromForm(): Address {
    const formRef = this.formRef();

    const address = createAddress({
      id: null,
      street: formRef?.form.value['manual-address-street'],
      streetNumber: formRef?.form.value['manual-address-streetNumber'],
      block: formRef?.form.value['manual-address-blockNumber'],
      doorNumber: formRef?.form.value['manual-address-doorNumber'],
      hint: null, // there is no adresshinweis
      postcode: formRef?.form.value['manual-address-post-code'],
      city: formRef?.form.value['manual-address-city'],
      country: null,
    });

    this.formAddress.set(address);

    return address;
  }

  validate(): boolean {
    const formRef = this.formRef();

    formRef?.fireValidation();
    return formRef?.form.valid;
  }

  private createFormModel(): void {
    const initialData = this.initialData();

    this.formModel = {
      updateOn: 'blur',
      inputs: [
        {
          name: 'row-1',
          inputs: [
            {
              name: 'manual-address-post-code',
              initialValue: initialData?.postcode ?? '',
              validators: [
                Validators.required,
                Validators.minLength(INPUT_MIN_LENGTH),
                Validators.maxLength(INPUT_LOWER_MAX_LENGTH),
                Validators.pattern('[A-Z0-9- ]*'),
              ],
              labelKey: 'shared.international-address-input.form.postCode.title',
              validationErrorLabelKey: 'shared.international-address-input.form.postCode.error',
              validate: true,
              dataType: 'text',
              maxLength: INPUT_LOWER_MAX_LENGTH,
              updateOn: 'change',
              inputClasses: 'col-12 col-md-4',
            },
            {
              name: 'manual-address-city',
              initialValue: initialData?.city ?? '',
              validators: [Validators.required, Validators.maxLength(INPUT_HIGHER_MAX_LENGTH)],
              labelKey: 'shared.international-address-input.form.city.title',
              validationErrorLabelKey: 'shared.international-address-input.form.city.error',
              validate: true,
              dataType: 'text',
              maxLength: INPUT_HIGHER_MAX_LENGTH,
              inputClasses: 'col-12 col-md-8',
            },
          ],
        },
        {
          name: 'row-2',
          inputs: [
            {
              name: 'manual-address-street',
              initialValue: initialData?.street ?? '',
              validators: [Validators.required, Validators.maxLength(INPUT_HIGHER_MAX_LENGTH)],
              labelKey: 'shared.international-address-input.form.street.title',
              validationErrorLabelKey: 'shared.international-address-input.form.street.error',
              validate: true,
              dataType: 'text',
              maxLength: INPUT_HIGHER_MAX_LENGTH,
              inputClasses: 'col-12 col-md-8',
            },
            {
              name: 'manual-address-streetNumber',
              initialValue: initialData?.streetNumber ?? '',
              validators: [Validators.required, Validators.maxLength(INPUT_LOWER_MAX_LENGTH)],
              labelKey: 'shared.international-address-input.form.streetNumber.title',
              validationErrorLabelKey: 'shared.international-address-input.form.streetNumber.error',
              validate: true,
              dataType: 'text',
              maxLength: INPUT_LOWER_MAX_LENGTH,
              inputClasses: 'col-12 col-md-4',
            },
          ],
        },
        {
          name: 'row-3',
          inputs: [
            {
              name: 'manual-address-blockNumber',
              initialValue: initialData?.block ?? '',
              validators: [Validators.maxLength(INPUT_LOWER_MAX_LENGTH)],
              labelKey: 'shared.international-address-input.form.blockNumber.title',
              validationErrorLabelKey: 'shared.international-address-input.form.blockNumber.error',
              validate: true,
              dataType: 'text',
              maxLength: INPUT_LOWER_MAX_LENGTH,
              inputClasses: 'col-12 col-md-6',
            },
            {
              name: 'manual-address-doorNumber',
              initialValue: initialData?.doorNumber ?? '',
              validators: [Validators.maxLength(INPUT_LOWER_MAX_LENGTH)],
              labelKey: 'shared.international-address-input.form.doorNumber.title',
              validationErrorLabelKey: 'shared.international-address-input.form.doorNumber.error',
              validate: true,
              dataType: 'text',
              maxLength: INPUT_LOWER_MAX_LENGTH,
              inputClasses: 'col-12 col-md-6',
            },
          ],
        },
      ],
    };
  }
}
